import apiClient from './apiClient';

// Get shipped consignments ready for bundle receipt
export const getShippedConsignments = async () => {
  const response = await apiClient.get('/sections/fob/shipped-consignments');
  return response.data;
};

// Start bundle receipt process
export const startBundleReceipt = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/start-bundle-receipt/${entryId}`);
  return response.data;
};

// Get bundle entry status
export const getBundleEntry = async (entryId) => {
  const response = await apiClient.get(`/sections/fob/bundle-entry/${entryId}`);
  return response.data;
};

// Scan a bundle barcode
export const scanBundle = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(
    `/sections/fob/scan-bundle/${entryId}`,
    null,
    { params: { bundle_barcode: bundleBarcode } }
  );
  return response.data;
};

// Complete bundle receipt
export const completeBundleReceipt = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/complete-bundle-receipt/${entryId}`);
  return response.data;
};

// Remove a scanned bundle
export const removeBundle = async (entryId, bundleBarcode) => {
  const response = await apiClient.delete(`/sections/fob/remove-bundle/${entryId}/${bundleBarcode}`);
  return response.data;
};

// =====================================================
// OPERATION 2: Sort No → USN Tagging
// =====================================================

// Get consignments ready for USN tagging
export const getUsnReadyConsignments = async () => {
  const response = await apiClient.get('/sections/fob/usn-ready-consignments');
  return response.data;
};

// Start sort entry process
export const startSortEntry = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/start-sort-entry/${entryId}`);
  return response.data;
};

// Get sort entry
export const getSortEntry = async (entryId) => {
  const response = await apiClient.get(`/sections/fob/sort-entry/${entryId}`);
  return response.data;
};

// Add a sort entry
export const addSortEntry = async (entryId, sortNo, actualQty, excess = 0, shortage = 0, remarks = null) => {
  const params = { sort_no: sortNo, actual_qty: actualQty, excess, shortage };
  if (remarks) params.remarks = remarks;
  
  const response = await apiClient.post(
    `/sections/fob/add-sort-entry/${entryId}`,
    null,
    { params }
  );
  return response.data;
};

// Remove a sort entry
export const removeSortEntry = async (entryId, sortNo) => {
  const response = await apiClient.delete(`/sections/fob/remove-sort-entry/${entryId}/${sortNo}`);
  return response.data;
};

// Confirm sort entries and assign USNs
export const confirmSortEntry = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/confirm-sort-entry/${entryId}`);
  return response.data;
};

// Mark USNs as printed (legacy - by sort number)
export const markUsnsPrinted = async (entryId, sortNo) => {
  const response = await apiClient.post(`/sections/fob/mark-usns-printed/${entryId}/${sortNo}`);
  return response.data;
};

// =====================================================
// OPERATION 2: Bundle-Centric Workflow APIs
// =====================================================

// Get vendors for FOB section
export const getVendors = async () => {
  const response = await apiClient.get('/sections/fob/vendors');
  return response.data;
};

// Get sort nos by vendor for FOB section (with design_name)
export const getSortNosByVendor = async (vendorName) => {
  const response = await apiClient.get('/sections/fob/sort-nos', {
    params: { vendor_name: vendorName }
  });
  return response.data;
};

// Get FG Barcode for a sort number from greige_sort_nos table
export const getSortFgBarcode = async (sortNo) => {
  const response = await apiClient.get(`/sections/fob/sort-fg-barcode/${encodeURIComponent(sortNo)}`);
  return response.data;
};

// Validate bundle for sort entry
export const validateBundleForSort = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(
    `/sections/fob/validate-bundle-for-sort/${entryId}`,
    null,
    { params: { bundle_barcode: bundleBarcode } }
  );
  return response.data;
};

// Add sort entry for a specific bundle
export const addBundleSort = async (entryId, bundleBarcode, sortNo, actualQty, poItemId = null, poQty = null) => {
  const response = await apiClient.post(
    `/sections/fob/add-bundle-sort/${entryId}`,
    null,
    { params: { bundle_barcode: bundleBarcode, sort_no: sortNo, actual_qty: actualQty,
      ...(poItemId ? { po_item_id: poItemId } : {}), ...(poQty ? { po_qty: poQty } : {}) } }
  );
  return response.data;
};

// Remove sort entry from a bundle
export const removeBundleSort = async (entryId, bundleBarcode, sortNo) => {
  const response = await apiClient.delete(
    `/sections/fob/remove-bundle-sort/${entryId}`,
    { params: { bundle_barcode: bundleBarcode, sort_no: sortNo } }
  );
  return response.data;
};

// Complete all bundles and generate summary
export const completeBundles = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/complete-bundles/${entryId}`);
  return response.data;
};

// Update Bill Qty for a sort number in summary
export const updateSummaryBillQty = async (entryId, sortNo, billQty, remarks = null) => {
  const params = { sort_no: sortNo, bill_qty: billQty };
  if (remarks) params.remarks = remarks;
  
  const response = await apiClient.post(
    `/sections/fob/update-summary-bill-qty/${entryId}`,
    null,
    { params }
  );
  return response.data;
};

// Confirm summary and assign USNs
export const confirmSummary = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/confirm-summary/${entryId}`);
  return response.data;
};

// Get USNs for a specific bundle (for printing)
export const getBundleUsns = async (entryId, bundleBarcode) => {
  const response = await apiClient.get(`/sections/fob/bundle-usns/${entryId}`, {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

// Get USNs for a specific Sort No across all bundles (NEW - for summary table hyperlink)
export const getSortUsns = async (entryId, sortNo) => {
  const response = await apiClient.get(`/sections/fob/sort-usns/${entryId}`, {
    params: { sort_no: sortNo }
  });
  return response.data;
};


// Mark bundle USNs as printed
export const markBundlePrinted = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/sections/fob/mark-bundle-printed/${entryId}`, null, {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

// Record a SUCCESSFUL print for a bundle — stamps last_printed_at + increments
// print_count (used by the Bundles Processed print-status column).
export const recordBundlePrint = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/sections/fob/record-bundle-print/${entryId}`, null, {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

// NEW: Assign USNs to a single bundle (one-bundle-at-a-time workflow)
export const assignBundleUsn = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/sections/fob/assign-bundle-usn/${entryId}`, null, {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

// NEW: Get bundle assignment progress (Assigned Qty vs Document Qty)
export const getBundleAssignmentProgress = async (entryId) => {
  const response = await apiClient.get(`/sections/fob/bundle-assignment-progress/${entryId}`);
  return response.data;
};

// Update document barcode
export const updateDocumentBarcode = async (entryId, newBarcode) => {
  const response = await apiClient.put(`/sections/fob/update-document-barcode/${entryId}`, null, {
    params: { new_barcode: newBarcode }
  });
  return response.data;
};

// Revoke USN assignments (NEW - for re-assignment with fixed logic)
export const revokeUsnAssignment = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/revoke-usn-assignment/${entryId}`);
  return response.data;
};

// Reset sort entry to in_progress (NEW - to fix prematurely completed entries)
export const resetSortEntry = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/reset-sort-entry/${entryId}`);
  return response.data;
};

// Get list of documents with USN assignments (NEW - for admin panel)
export const getUsnAssignmentList = async () => {
  const response = await apiClient.get('/sections/fob/usn-assignment-list');
  return response.data;
};


// =====================================================
// OPERATION 3: Assortment
// =====================================================


// =====================================================
// FOB SECTION REPORTS
// =====================================================

export const getFobOperations = async () => {
  const response = await apiClient.get('/sections/fob/reports/operations');
  return response.data;
};

export const getFobEnhancedSummary = async (params = {}) => {
  const response = await apiClient.get('/sections/fob/reports/enhanced-summary', { params });
  return response.data;
};

export const getFobTransactions = async (params = {}) => {
  const response = await apiClient.get('/sections/fob/reports/transactions', { params });
  return response.data;
};

export const getFobClosingStock = async (params = {}) => {
  const response = await apiClient.get('/sections/fob/reports/closing-stock', { params });
  return response.data;
};

export const getFobDrillDownData = async (date, type, params = {}) => {
  const response = await apiClient.get(`/sections/fob/reports/drill-down/${date}/${type}`, { params });
  return response.data;
};

// =====================================================
// INTERNAL TRANSFER - Pending Shipments
// =====================================================

// Get pending shipments (transfers accepted by receiving section, waiting for FOB to ship)
export const getPendingShipments = async () => {
  const response = await apiClient.get('/internal-transfer/pending-requests');
  return response.data;
};

// Confirm shipment of an internal transfer
export const confirmShipment = async (transferId) => {
  const response = await apiClient.post('/internal-transfer/confirm-shipment', { transfer_id: transferId });
  return response.data;
};

// ===== Combined Assortment Packing (multi-staff) =====
export const validateAssortmentPackingUsn = async (usnCode) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/validate-usn', null, {
    params: { usn_code: usnCode }
  });
  return response.data;
};

export const submitAssortmentPacking = async (staffList, bundleBarcode, usnCodes) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/submit-batch', {
    staff_list: staffList,
    bundle_barcode: bundleBarcode,
    usn_codes: usnCodes
  });
  return response.data;
};

// ===== FOB Section Settings (Pre-pasted USN mode) =====
export const getFobSettings = async () => {
  const response = await apiClient.get('/sections/fob/settings');
  return response.data;
};

export const updateFobSettings = async (prepastedUsnMode) => {
  const response = await apiClient.put('/sections/fob/settings', {
    prepasted_usn_mode: prepastedUsnMode
  });
  return response.data;
};

// ===== Pre-pasted Assortment Packing (scan pre-printed stickers) =====
export const getPrepastedDocuments = async () => {
  const response = await apiClient.get('/sections/fob/assortment-packing/prepasted/documents');
  return response.data;
};

export const getPrepastedBundles = async (entryId) => {
  const response = await apiClient.get(`/sections/fob/assortment-packing/prepasted/bundles/${entryId}`);
  return response.data;
};

export const validatePrepastedUsn = async ({ usnCode, entryId, bundleBarcode, sortNo, sessionCount = 0 }) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/prepasted/validate-usn', {
    usn_code: usnCode,
    entry_id: entryId,
    bundle_barcode: bundleBarcode,
    sort_no: sortNo,
    session_count: sessionCount
  });
  return response.data;
};

export const submitPrepastedPacking = async (staffList, outputBundleBarcode, scans) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/prepasted/submit-batch', {
    staff_list: staffList,
    output_bundle_barcode: outputBundleBarcode,
    scans: scans
  });
  return response.data;
};

// ===== Pre-pasted DOCUMENT-LEVEL mode (redesign 2026-06) =====
export const getPrepastedDocumentsDoc = async () => {
  const response = await apiClient.get('/sections/fob/assortment-packing/prepasted/documents-doc');
  return response.data;
};

export const getPrepastedSorts = async (entryId) => {
  const response = await apiClient.get(`/sections/fob/assortment-packing/prepasted/sorts/${entryId}`);
  return response.data;
};

export const validatePrepastedUsnDoc = async ({ usnCode, entryId, sortNo, sessionCount = 0, allowExcess = false }) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/prepasted/validate-usn-doc', {
    usn_code: usnCode,
    entry_id: entryId,
    sort_no: sortNo,
    session_count: sessionCount,
    allow_excess: allowExcess,
  });
  return response.data;
};

export const submitPrepastedPackingDoc = async (staffList, outputBundleBarcode, entryId, scans, excessApproval = null) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/prepasted/submit-batch-doc', {
    staff_list: staffList,
    output_bundle_barcode: outputBundleBarcode,
    entry_id: entryId,
    scans: scans,
    excess_approval: excessApproval,
  });
  return response.data;
};

// ===== FOB Force-Close (admin) =====
export const getForceCloseDocuments = async () => {
  const response = await apiClient.get('/sections/fob/assortment-packing/prepasted/force-close/documents');
  return response.data;
};

export const forceCloseDocument = async (entryId, reason) => {
  const response = await apiClient.post(`/sections/fob/assortment-packing/prepasted/force-close/${entryId}`, { reason });
  return response.data;
};

export const reopenDocument = async (entryId) => {
  const response = await apiClient.post(`/sections/fob/assortment-packing/prepasted/reopen/${entryId}`);
  return response.data;
};

// ===== FOB Damage report + return-to-supplier =====
export const getFobDamageReport = async (params = {}) => {
  const response = await apiClient.get('/sections/fob/assortment-packing/damage/report', { params });
  return response.data;
};

export const exportFobDamageCsv = async (params = {}) => {
  const response = await apiClient.get('/sections/fob/assortment-packing/damage/export', { params, responseType: 'blob' });
  return response.data;
};

export const returnFobDamageToSupplier = async (payload) => {
  const response = await apiClient.post('/sections/fob/assortment-packing/damage/return-to-supplier', payload);
  return response.data;
};